/**
 * PWA Authentication Redirect Utilities
 *
 * Handles authentication redirects for PWA contexts, preserving
 * the subdomain and return URL for seamless re-authentication.
 */
import { isPWA } from './deviceDetection';
import { detectSubdomain, getMainDomainUrl, SUBDOMAINS } from './subdomainUtils';
import { sessionMonitor } from '../services/SessionMonitorService';
import RedirectService from '../services/RedirectService';
import { logger } from "./logger";
// import { logger } from "./logger";
export interface PWAAuthContext {
    isPWA: boolean;
    subdomain: string | null;
    returnUrl: string;
    storedAt: number;
}
/**
 * Check if we're in a PWA context that needs special handling
 */
export function isPWAContext(): boolean {
    const isPWAMode = isPWA();
    const subdomainInfo = detectSubdomain();
    // Check if we're on a PWA-specific subdomain
    const pwaSubdomains = [
        SUBDOMAINS.TEACH,
        SUBDOMAINS.ME,
        SUBDOMAINS.BACKOFFICE,
        SUBDOMAINS.K9
    ];
    return isPWAMode || (subdomainInfo.subdomain !== null && pwaSubdomains.includes(subdomainInfo.subdomain as any));
}
/**
 * Get user-friendly name for PWA subdomain
 */
export function getPWASubdomainName(subdomain: string | null): string {
    switch (subdomain) {
        case SUBDOMAINS.TEACH:
            return 'Instructor Portal';
        case SUBDOMAINS.ME:
            return 'Student Portal';
        case SUBDOMAINS.BACKOFFICE:
            return 'Backoffice';
        case SUBDOMAINS.K9:
            return 'K9 Operations';
        default:
            return 'App';
    }
}
/**
 * Redirect to login with PWA context preservation
 */
export function redirectToLoginWithContext(): void {
    const returnPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    logger.auth('🔐 Redirecting to login from:', { "arg0": returnPath });
    // Store path-only return target (never full href with nested ?redirect=)
    void RedirectService.storeCurrentUrlAsRedirect(returnPath);
    // If in PWA context, store additional context
    if (isPWAContext()) {
        sessionMonitor.storePWAContext();
    }
    // Redirect to login page on main domain
    const loginUrl = getMainDomainUrl('/login');
    logger.debug('🔐 Redirecting to:', { "arg0": loginUrl });
    window.location.href = loginUrl;
}
/**
 * Handle post-login redirect back to PWA
 * Call this after successful authentication
 */
export async function handlePostLoginRedirect(): Promise<void> {
    // Check if we have a stored PWA context
    const pwaContext = await sessionMonitor.getStoredPWAContext();
    if (pwaContext && pwaContext.returnUrl) {
        logger.debug('🔐 Restoring PWA context, redirecting to:', { "arg0": pwaContext.returnUrl });
        // Clear the stored context
        sessionMonitor.clearPWAContext();
        // Redirect back to the PWA
        window.location.href = pwaContext.returnUrl;
        return;
    }
    // Check if RedirectService has a stored URL
    const redirectUrl = await RedirectService.getRedirectUrl({ clearAfterRetrieval: true });
    if (redirectUrl) {
        logger.debug('🔐 Redirecting to stored URL:', { "arg0": redirectUrl });
        window.location.href = redirectUrl;
        return;
    }
    // Default: go to dashboard
    logger.debug('🔐 No stored redirect, going to dashboard');
    window.location.href = '/dashboard';
}
/**
 * Save work before redirecting to login
 * Used when user clicks "Save Work & Login"
 */
export function saveWorkAndRedirect(pageKey: string, formData: any): void {
    logger.debug('💾 Saving work before redirect for page:', { "arg0": pageKey });
    try {
        // Save the form data
        sessionMonitor.saveFormState(pageKey, formData);
        // Then redirect to login
        redirectToLoginWithContext();
    }
    catch (error) {
        logger.error('Failed to save work before redirect:', error, {});
        // Still redirect even if save fails
        redirectToLoginWithContext();
    }
}
/**
 * Check if there is saved work from before session expiration
 */
export function hasSavedWork(pageKey: string): boolean {
    return sessionMonitor.hasSavedFormState(pageKey);
}
/**
 * Get saved work data
 */
export function getSavedWork(pageKey: string): any | null {
    return sessionMonitor.getSavedFormState(pageKey);
}
/**
 * Clear saved work after successful restoration
 */
export function clearSavedWork(pageKey: string): void {
    sessionMonitor.clearFormState(pageKey);
}
/**
 * Get message for session expired UI based on context
 */
export function getSessionExpiredMessage(): {
    title: string;
    message: string;
    details: string[];
} {
    const subdomainInfo = detectSubdomain();
    const isPWAMode = isPWA();
    const pwaName = getPWASubdomainName(subdomainInfo.subdomain);
    if (isPWAMode || isPWAContext()) {
        return {
            title: 'Session Expired',
            message: `Your session in the ${pwaName} has expired. You'll be redirected to the main site to log in again.`,
            details: [
                'You will be redirected to the main authentication page',
                'After logging in, you\'ll be returned to where you left off',
                'Any work in progress will be preserved if you choose to save it'
            ]
        };
    }
    return {
        title: 'Session Expired',
        message: 'Your session has expired. Please log in again to continue.',
        details: [
            'You will be redirected to the login page',
            'After logging in, you\'ll be returned to this page',
            'Any unsaved changes may be lost'
        ]
    };
}
export default {
    isPWAContext,
    getPWASubdomainName,
    redirectToLoginWithContext,
    handlePostLoginRedirect,
    saveWorkAndRedirect,
    hasSavedWork,
    getSavedWork,
    clearSavedWork,
    getSessionExpiredMessage
};
